// [목적] /festivals — 상태·검색·지역·정렬로 강원 축제를 찾는 전체 목록 화면
// [이유] 홈의 축제 위젯을 넘어, 사용자가 "지금 갈 수 있는가 → 어디인가 → 어떤 축제인가" 순서로
//        빠르게 범위를 좁히고 상세 화면으로 이동할 수 있는 독립 탐색 경험을 제공한다.
// [사이드 이팩트] GET /festivals 목록 API 는 category/region/month 필터와 통계 집계 엔드포인트를
//               제공하지 않는다(limit 만 지원, 최대 36). 그래서:
//               · 카테고리 필터는 만들지 않는다(백엔드에 category 필드 자체가 없음 — 목업의 "공연·전시·뮤직"
//                 탭은 실제 데이터로 채울 수 없어 생략하고, 대신 실제로 존재하는 status(진행중/예정/종료)로
//                 필터한다.
//               · 상태 건수와 시·군 선택지는 limit=36 응답을 클라이언트에서 집계한다(서버 집계 엔드포인트 없음).
//               · 상세 페이지(FestivalDetailPage)는 GET /festivals/:id 단건 API(useFestival)를 직접 호출한다 —
//                 목록 limit(36) 밖의 축제도 열리며 이 화면의 목록 캐시와 무관하다.
//                 단, 이 목록 화면 자체는 여전히 최대 36건까지만 보인다(페이지네이션 미지원 — 후속 과제).
// [수정 시 고려사항] 백엔드에 검색/지역/통계 엔드포인트가 추가되면 클라이언트 필터를 서버 쿼리로 교체할 것.

import { useMemo } from 'react';
import { Box, ButtonBase, InputBase, MenuItem, Select, Skeleton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { tokens } from '@gangwoncanvas/ui';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { TopBar } from '../components/layout/TopBar.js';
import { useShell } from '../hooks/useShell.js';
import { useFestivals } from '../hooks/useFestivals.js';
import { FESTIVAL_STATUS_META } from '../utils/festivalStatusMeta.js';
import { safeCssUrl } from '../utils/safeCssUrl.js';
import {
  formatFestivalLocation,
  formatFestivalPeriodWithYear,
  truncateOverview,
  type Festival,
  type FestivalStatus,
} from '../api/festivals.client.js';

const c = tokens.color;
const L = 'light' as const;
const INK_HI = c.ink.high[L];
const INK_MID = c.ink.mid[L];
const INK_LOW = c.ink.low[L];
const BORDER = c.line.base[L];
const PAPER0 = c.paper[0][L];
const PAPER1 = c.paper[1][L];
const PAPER2 = c.paper[2][L];
const BRAND = c.brand[500][L];
const WARM = c.warm[500][L];

// [수정: 2026-09-20] 세 화면에 복붙돼 있던 STATUS_META 를 utils/festivalStatusMeta 로 통합.
// 소형 배지 텍스트가 AA 미달이던 문제도 거기서 textColor 로 해결된다.
const STATUS_META = FESTIVAL_STATUS_META;

/** [목적] 상태 필터 탭 — 백엔드에 category 필드가 없어 실제로 존재하는 status 기준으로만 구성 */
type StatusFilter = 'all' | FestivalStatus;
const STATUS_FILTERS: ReadonlyArray<{ id: StatusFilter; label: string }> = [
  { id: 'all', label: '전체' },
  { id: 'ongoing', label: '진행 중' },
  { id: 'upcoming', label: '예정' },
  { id: 'unknown', label: '일정 확인 중' },
  { id: 'ended', label: '종료' },
];

type SortOption = 'recommended' | 'latest' | 'name';
const SORT_OPTIONS: ReadonlyArray<{ id: SortOption; label: string }> = [
  { id: 'recommended', label: '추천순' },
  { id: 'latest', label: '최근 일정순' },
  { id: 'name', label: '이름순' },
];

/**
 * [목적] 축제 시작일과 종료일 사이에 포함되는 모든 YYYY-MM 값을 만든다.
 * [이유] 6월부터 10월까지 이어지는 축제가 시작 월인 6월에서만 검색되는 문제를 막는다.
 * [사이드 이팩트] 없음.
 * [수정 시 고려사항] 비정상 날짜나 종료일이 시작일보다 빠른 데이터는 기간 필터 후보에서 제외한다.
 */
function getFestivalMonths(startDate: string, endDate: string): string[] {
  if (!/^\d{8}$/.test(startDate) || !/^\d{8}$/.test(endDate)) return [];

  const startYear = Number(startDate.slice(0, 4));
  const startMonth = Number(startDate.slice(4, 6));
  const endYear = Number(endDate.slice(0, 4));
  const endMonth = Number(endDate.slice(4, 6));
  if (startMonth < 1 || startMonth > 12 || endMonth < 1 || endMonth > 12) return [];

  const startIndex = startYear * 12 + startMonth - 1;
  const endIndex = endYear * 12 + endMonth - 1;
  if (endIndex < startIndex) return [];

  return Array.from({ length: Math.min(endIndex - startIndex + 1, 120) }, (_, index) => {
    const monthIndex = startIndex + index;
    const year = Math.floor(monthIndex / 12);
    const month = (monthIndex % 12) + 1;
    return `${year}-${String(month).padStart(2, '0')}`;
  });
}

function formatPeriodLabel(period: string): string {
  const [year, month] = period.split('-');
  return `${year}년 ${Number(month)}월`;
}

/**
 * [목적] limit=36 응답에서 상태별 건수와 실제 선택 가능한 시·군 목록을 집계
 * [이유] 장식용 월별 그래프보다 사용자가 "지금 갈 수 있는지, 어느 지역인지" 바로 좁히는 정보가 우선이다.
 * [사이드 이팩트] 없음(순수 함수) — festivals 배열이 바뀔 때만 재계산(useMemo).
 * [수정 시 고려사항] 서버 필터/집계 API가 추가되면 전체 데이터 기준 집계값으로 교체해야 한다.
 */
function useFestivalStats(festivals: Festival[] | undefined) {
  return useMemo(() => {
    const regions = new Set<string>();
    const periods = new Set<string>();
    const statusCounts: Record<StatusFilter, number> = {
      all: festivals?.length ?? 0,
      ongoing: 0,
      upcoming: 0,
      unknown: 0,
      ended: 0,
    };
    for (const f of festivals ?? []) {
      statusCounts[f.status] += 1;
      const region = formatFestivalLocation(f.addr1);
      if (region) regions.add(region);
      getFestivalMonths(f.startDate, f.endDate).forEach((period) => periods.add(period));
    }
    return {
      statusCounts,
      regions: [...regions].sort((a, b) => a.localeCompare(b, 'ko')),
      periods: [...periods].sort((a, b) => b.localeCompare(a)),
    };
  }, [festivals]);
}

/**
 * [목적] 카드 일정에 연도를 포함해 여러 해의 축제가 섞여도 날짜를 오해하지 않게 한다.
 * [이유] 기존 M.D 표기는 2024·2025·2026 데이터가 한 화면에 있을 때 최신 축제처럼 보일 수 있었다.
 * [사이드 이팩트] 없음.
 * [수정 시 고려사항] API 날짜 포맷이 YYYYMMDD에서 바뀌면 공용 포맷 유틸로 이동한다.
 */
// [수정: 2026-09-20] 동일 포맷을 FestivalDetailPage 도 써야 해서 festivals.client 로 올리고
// 여기서는 재수출만 한다(기존 호출부 이름 유지).
const formatFestivalCardPeriod = formatFestivalPeriodWithYear;

export function FestivalsPage(): JSX.Element {
  const { viewport, openSidebar } = useShell();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: festivals, isLoading, error, refetch } = useFestivals(36);
  const stats = useFestivalStats(festivals);

  const statusParam = searchParams.get('status');
  const statusFilter: StatusFilter = STATUS_FILTERS.some((item) => item.id === statusParam)
    ? (statusParam as StatusFilter)
    : 'all';
  const query = searchParams.get('q') ?? '';
  const regionParam = searchParams.get('region');
  const region = regionParam && stats.regions.includes(regionParam) ? regionParam : 'all';
  const periodParam = searchParams.get('period');
  const period = periodParam && stats.periods.includes(periodParam) ? periodParam : 'all';
  const sortParam = searchParams.get('sort');
  const sort: SortOption = SORT_OPTIONS.some((item) => item.id === sortParam)
    ? (sortParam as SortOption)
    : 'recommended';

  /**
   * [목적] 검색·필터·정렬 상태를 URL에 기록해 새로고침과 링크 공유 뒤에도 탐색 맥락을 보존한다.
   * [이유] 목록으로 돌아왔을 때 조건이 초기화되면 사용자가 같은 탐색을 반복해야 한다.
   * [사이드 이팩트] 현재 history 항목을 replace한다.
   * [수정 시 고려사항] 서버 필터가 생기면 이 파라미터 이름을 API 쿼리와 맞춘다.
   */
  const updateFilter = (
    key: 'status' | 'q' | 'region' | 'period' | 'sort',
    value: string,
    defaultValue: string,
  ) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === defaultValue) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const normalizedQuery = query.trim().toLocaleLowerCase('ko');
  const filtered = useMemo(() => {
    const matches = (festivals ?? []).filter((festival) => {
      const location = formatFestivalLocation(festival.addr1) ?? '';
      const matchesStatus = statusFilter === 'all' || festival.status === statusFilter;
      const matchesRegion = region === 'all' || location === region;
      const matchesPeriod =
        period === 'all' ||
        getFestivalMonths(festival.startDate, festival.endDate).includes(period);
      const searchable =
        `${festival.title} ${location} ${festival.overview ?? ''}`.toLocaleLowerCase('ko');
      return (
        matchesStatus &&
        matchesRegion &&
        matchesPeriod &&
        (!normalizedQuery || searchable.includes(normalizedQuery))
      );
    });

    if (sort === 'latest')
      return [...matches].sort((a, b) => b.startDate.localeCompare(a.startDate));
    if (sort === 'name') return [...matches].sort((a, b) => a.title.localeCompare(b.title, 'ko'));
    return matches;
  }, [festivals, normalizedQuery, period, region, sort, statusFilter]);

  const hasActiveFilters =
    statusFilter !== 'all' || region !== 'all' || period !== 'all' || Boolean(normalizedQuery);
  const resetFilters = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('status');
    next.delete('q');
    next.delete('region');
    next.delete('period');
    setSearchParams(next, { replace: true });
  };

  const cols = viewport.isDesktop ? 3 : viewport.isTablet ? 2 : 1;

  return (
    <>
      <TopBar title="강원 축제" mobile={viewport.isMobile} onBurger={openSidebar} />
      <Box
        data-testid="festivals-content"
        sx={{
          // [목적] 결과 건수와 무관하게 페이지 본문 폭을 유지한다.
          // [이유] flex column 자식에 mx:auto만 두면 빈 상태에서 shrink-to-fit이 적용되어 전체/종료 필터 전환 시 화면이 좌우로 이동한다.
          // [사이드 이펙트] 본문은 항상 가용 폭을 사용하되 1100px maxWidth는 유지한다.
          // [수정 시 고려사항] 패딩을 바꾸더라도 border-box 기준의 100% 폭을 제거하지 말 것.
          width: '100%',
          p: {
            xs: `${tokens.spacing[6]}px ${tokens.spacing[4]}px 60px`,
            md: `${tokens.spacing[8]}px ${tokens.spacing[8]}px 60px`,
          },
          maxWidth: 1100,
          mx: 'auto',
        }}
      >
        <Typography
          component="h2"
          sx={{ fontSize: 24, fontWeight: 800, color: INK_HI, letterSpacing: '-0.4px', mb: '4px' }}
        >
          가고 싶은 축제를 찾아보세요
        </Typography>
        <Typography sx={{ fontSize: 14, color: INK_MID, mb: `${tokens.spacing[6]}px` }}>
          일정과 지역을 비교하고, 마음에 드는 축제를 여행 계획으로 이어가세요.
        </Typography>

        {isLoading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <Skeleton
              variant="rounded"
              height={136}
              sx={{ borderRadius: `${tokens.radius.l}px` }}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '14px' }}>
              {Array.from({ length: cols * 2 }).map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  height={260}
                  sx={{ borderRadius: `${tokens.radius.m}px` }}
                />
              ))}
            </Box>
          </Box>
        )}

        {!isLoading && error && (
          <Box
            role="alert"
            sx={{ p: `${tokens.spacing[10]}px ${tokens.spacing[5]}px`, textAlign: 'center' }}
          >
            <Typography
              sx={{ color: INK_HI, fontSize: 15, fontWeight: 700, mb: `${tokens.spacing[2]}px` }}
            >
              축제 정보를 불러오지 못했어요
            </Typography>
            <Typography sx={{ color: INK_MID, fontSize: 13, mb: `${tokens.spacing[4]}px` }}>
              네트워크 상태를 확인한 뒤 다시 시도해 주세요.
            </Typography>
            <ButtonBase
              onClick={() => void refetch()}
              aria-label="축제 정보 다시 불러오기"
              sx={{
                minHeight: 44,
                px: `${tokens.spacing[4]}px`,
                border: `1px solid ${BORDER}`,
                borderRadius: `${tokens.radius.m}px`,
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              다시 시도
            </ButtonBase>
          </Box>
        )}

        {!isLoading && !error && (
          <>
            {/* ── 탐색 도구: 상태 → 검색/지역 순으로 사용자의 선택 범위를 좁힌다 ── */}
            <Box
              component="section"
              aria-label="축제 찾기"
              sx={{
                bgcolor: PAPER0,
                border: `1px solid ${BORDER}`,
                borderRadius: `${tokens.radius.l}px`,
                p: { xs: `${tokens.spacing[4]}px`, md: `${tokens.spacing[5]}px` },
                mb: `${tokens.spacing[6]}px`,
              }}
            >
              <Box
                role="group"
                aria-label="축제 상태 필터"
                sx={{
                  display: 'flex',
                  gap: `${tokens.spacing[2]}px`,
                  flexWrap: 'wrap',
                  mb: `${tokens.spacing[4]}px`,
                }}
              >
                {STATUS_FILTERS.map((item) => {
                  const active = statusFilter === item.id;
                  // [수정: 디자인 개편 2026-09-20] 결과 0건인 필터를 활성 필터와 똑같은 무게로 보여줘
                  // 누르고 나서야 빈 화면을 만나게 됐다. 미리 흐리게 해 헛걸음을 줄인다.
                  // disabled 가 아니라 선택은 가능하고(빈 상태 안내가 뜨는 게 정상 동작),
                  // 현재 선택된 필터라면 흐리게 하지 않는다.
                  const isEmpty = stats.statusCounts[item.id] === 0 && !active;
                  return (
                    <ButtonBase
                      key={item.id}
                      onClick={() => updateFilter('status', item.id, 'all')}
                      aria-label={`${item.label} 축제 ${stats.statusCounts[item.id]}개`}
                      aria-pressed={active}
                      sx={{
                        minHeight: 44,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: `${tokens.spacing[2]}px`,
                        bgcolor: active ? INK_HI : PAPER0,
                        color: active ? PAPER0 : INK_MID,
                        border: `1px solid ${active ? INK_HI : BORDER}`,
                        borderRadius: `${tokens.radius.xl}px`,
                        px: `${tokens.spacing[4]}px`,
                        fontSize: 13,
                        fontWeight: 700,
                        opacity: isEmpty ? 0.45 : 1,
                        transition: `opacity ${tokens.motion.fast.duration}ms, background-color ${tokens.motion.fast.duration}ms`,
                        '&:hover': { bgcolor: active ? INK_HI : PAPER1, opacity: 1 },
                        '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                      }}
                    >
                      <Box component="span">{item.label}</Box>
                      <Box
                        component="span"
                        sx={{
                          fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                          opacity: active ? 0.75 : 0.6,
                        }}
                      >
                        {stats.statusCounts[item.id]}
                      </Box>
                    </ButtonBase>
                  );
                })}
              </Box>

              {stats.statusCounts.ongoing === 0 && stats.statusCounts.upcoming === 0 && (
                /* 진행·예정 일정이 없을 때 날짜 결측 축제를 종료 축제로 오인하지 않도록 안내한다. */
                <Typography
                  role="status"
                  sx={{
                    fontSize: 12.5,
                    lineHeight: 1.55,
                    color: INK_MID,
                    bgcolor: PAPER2,
                    borderLeft: `3px solid ${alpha(BRAND, 0.4)}`,
                    borderRadius: `4px ${tokens.radius.m}px ${tokens.radius.m}px 4px`,
                    p: `${tokens.spacing[2]}px ${tokens.spacing[3]}px`,
                    mb: `${tokens.spacing[4]}px`,
                  }}
                >
                  {stats.statusCounts.unknown > 0
                    ? '현재 공개된 진행·예정 일정이 없어 일정 확인 중인 축제를 보여드리고 있어요.'
                    : '현재 공개된 진행·예정 축제가 없어 지난 축제를 보여드리고 있어요.'}
                </Typography>
              )}

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'minmax(240px, 1fr) 180px 160px' },
                  gap: `${tokens.spacing[3]}px`,
                }}
              >
                <Box
                  sx={{
                    minHeight: 44,
                    display: 'flex',
                    alignItems: 'center',
                    gap: `${tokens.spacing[2]}px`,
                    border: `1px solid ${BORDER}`,
                    borderRadius: `${tokens.radius.m}px`,
                    px: `${tokens.spacing[4]}px`,
                  }}
                >
                  <Box component="span" aria-hidden="true" sx={{ color: INK_LOW, fontSize: 16 }}>
                    ⌕
                  </Box>
                  <InputBase
                    value={query}
                    onChange={(event) => updateFilter('q', event.target.value, '')}
                    placeholder="축제 이름이나 키워드 검색"
                    inputProps={{ 'aria-label': '축제 검색' }}
                    sx={{ flex: 1, fontSize: 14, color: INK_HI }}
                  />
                  {query && (
                    <ButtonBase
                      onClick={() => updateFilter('q', '', '')}
                      aria-label="검색어 지우기"
                      sx={{
                        minWidth: 44,
                        minHeight: 44,
                        color: INK_LOW,
                        fontSize: 18,
                        borderRadius: `${tokens.radius.s}px`,
                      }}
                    >
                      ×
                    </ButtonBase>
                  )}
                </Box>

                <Select
                  value={region}
                  onChange={(event) => updateFilter('region', String(event.target.value), 'all')}
                  size="small"
                  inputProps={{ 'aria-label': '시군 선택' }}
                  sx={{
                    minHeight: 44,
                    bgcolor: PAPER0,
                    border: `1px solid ${BORDER}`,
                    borderRadius: `${tokens.radius.m}px`,
                    fontSize: 13,
                    color: INK_HI,
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  }}
                >
                  <MenuItem value="all" sx={{ fontSize: 13 }}>
                    모든 지역
                  </MenuItem>
                  {stats.regions.map((item) => (
                    <MenuItem key={item} value={item} sx={{ fontSize: 13 }}>
                      {item.replace('강원특별자치도 ', '')}
                    </MenuItem>
                  ))}
                </Select>

                <Select
                  value={period}
                  onChange={(event) => updateFilter('period', String(event.target.value), 'all')}
                  size="small"
                  inputProps={{ 'aria-label': '기간 선택' }}
                  sx={{
                    minHeight: 44,
                    bgcolor: PAPER0,
                    border: `1px solid ${BORDER}`,
                    borderRadius: `${tokens.radius.m}px`,
                    fontSize: 13,
                    color: INK_HI,
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  }}
                >
                  <MenuItem value="all" sx={{ fontSize: 13 }}>
                    전체 기간
                  </MenuItem>
                  {stats.periods.map((item) => (
                    <MenuItem key={item} value={item} sx={{ fontSize: 13 }}>
                      {formatPeriodLabel(item)}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>

            {/* ── 결과 요약 + 정렬 ─────────────────────────────────────── */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: `${tokens.spacing[3]}px`,
                mb: `${tokens.spacing[4]}px`,
              }}
            >
              <Box>
                <Typography component="h3" sx={{ fontSize: 16, fontWeight: 800, color: INK_HI }}>
                  축제 {filtered.length}개
                </Typography>
                {hasActiveFilters && (
                  <ButtonBase
                    onClick={resetFilters}
                    aria-label="검색과 필터 초기화"
                    sx={{
                      mt: `${tokens.spacing[1]}px`,
                      color: BRAND,
                      fontSize: 12,
                      fontWeight: 700,
                      minHeight: 28,
                    }}
                  >
                    필터 초기화
                  </ButtonBase>
                )}
              </Box>
              <Select
                value={sort}
                onChange={(event) =>
                  updateFilter('sort', String(event.target.value), 'recommended')
                }
                size="small"
                inputProps={{ 'aria-label': '축제 정렬 기준' }}
                sx={{
                  minHeight: 44,
                  minWidth: 124,
                  bgcolor: PAPER0,
                  border: `1px solid ${BORDER}`,
                  borderRadius: `${tokens.radius.m}px`,
                  fontSize: 13,
                  color: INK_HI,
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
                }}
              >
                {SORT_OPTIONS.map((item) => (
                  <MenuItem key={item.id} value={item.id} sx={{ fontSize: 13 }}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* ── 카드 그리드 ───────────────────────────────────────────── */}
            {filtered.length === 0 ? (
              <Box
                sx={{
                  textAlign: 'center',
                  p: `${tokens.spacing[12]}px ${tokens.spacing[5]}px`,
                  border: `1px dashed ${BORDER}`,
                  borderRadius: `${tokens.radius.l}px`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: INK_HI,
                    mb: `${tokens.spacing[2]}px`,
                  }}
                >
                  조건에 맞는 축제가 없어요
                </Typography>
                <Typography sx={{ fontSize: 13, color: INK_MID, mb: `${tokens.spacing[4]}px` }}>
                  검색어를 줄이거나 다른 상태·지역을 선택해 보세요.
                </Typography>
                <ButtonBase
                  onClick={resetFilters}
                  aria-label="모든 축제 다시 보기"
                  sx={{
                    minHeight: 44,
                    px: `${tokens.spacing[4]}px`,
                    bgcolor: INK_HI,
                    color: PAPER0,
                    borderRadius: `${tokens.radius.m}px`,
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  모든 축제 보기
                </ButtonBase>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                  gap: `${tokens.spacing[4]}px`,
                  // [수정: 디자인 개편 2026-09-20] 기본값 stretch 때문에 overview 가 없는 카드까지
                  // 같은 행에서 가장 큰 카드 높이로 늘어나 카드 아래 1/3 이 통째로 빈 면이 됐다.
                  // 카드는 자기 내용 높이만 차지하게 하고 여백은 그리드 바깥으로 둔다.
                  alignItems: 'start',
                }}
              >
                {filtered.map((f) => (
                  <FestivalCard
                    key={f.contentId}
                    festival={f}
                    onOpen={() => navigate(`/festivals/${f.contentId}`)}
                  />
                ))}
              </Box>
            )}
          </>
        )}
      </Box>
    </>
  );
}

function FestivalCard({
  festival,
  onOpen,
}: {
  festival: Festival;
  onOpen: () => void;
}): JSX.Element {
  const meta = STATUS_META[festival.status];
  const ended = festival.status === 'ended';
  const period = formatFestivalCardPeriod(festival.startDate, festival.endDate);
  const location = formatFestivalLocation(festival.addr1);
  const overview = truncateOverview(festival.overview, 60);
  // [수정] url(${firstImage}) 직접 보간은 `)`·`;` 로 CSS 선언을 탈출할 수 있어 safeCssUrl 로 http(s) 가드 + 이스케이프(보안 Low-1)
  const imageCss = safeCssUrl(festival.firstImage);

  return (
    <ButtonBase
      onClick={onOpen}
      aria-label={`${festival.title} 축제${ended ? ' (종료)' : ''}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        textAlign: 'left',
        bgcolor: PAPER0,
        border: `1px solid ${BORDER}`,
        borderRadius: `${tokens.radius.l}px`,
        overflow: 'hidden',
        // [수정: 디자인 개편 2026-09-20] minHeight:'100%' + 그리드 stretch 조합이 카드를 강제로
        // 늘렸다. 그리드를 alignItems:'start' 로 바꾸면서 이 강제 높이도 해제한다.
        minHeight: 0,
        // 상태 배지를 썸네일 위에 오버레이하기 위한 기준점
        position: 'relative',
        transition: `transform ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}, border-color ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}, box-shadow ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}`,
        '&:hover': {
          borderColor: BRAND,
          boxShadow: tokens.shadow.card,
          transform: 'translateY(-2px)',
        },
        // 썸네일 줄 — 축제는 이미지로 고르는 콘텐츠라 호버 피드백을 이미지에도 준다.
        '&:hover .gc-festival-thumb': { transform: 'scale(1.05)' },
        '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
      }}
    >
      <Box
        aria-hidden="true"
        className="gc-festival-thumb"
        sx={{
          // [수정: 디자인 개편 2026-09-20] 128 → 168px. 축제 카드는 사진이 선택 근거인데
          // 썸네일이 본문보다 작아 카드가 "텍스트 목록"처럼 보였다.
          height: tokens.spacing[16] * 2 + tokens.spacing[10],
          flexShrink: 0,
          background: imageCss
            ? `${imageCss} center/cover`
            : `linear-gradient(135deg, ${alpha(WARM, 0.28)}, ${alpha(BRAND, 0.16)})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 28,
          transition: `transform ${tokens.motion.slow.duration}ms ${tokens.motion.fast.easing}`,
        }}
      >
        {!imageCss && '🎪'}
      </Box>
      {/* [수정: 디자인 개편 2026-09-20] 상태 배지를 본문 맨 위에서 썸네일 오버레이로 이동.
          배지 하나가 본문 한 줄(22px + 여백 8px)을 통째로 쓰고 있었다.
          배경색은 상태별 토큰 그대로 두고, 이미지 위에서도 읽히도록 그림자만 더한다. */}
      <Box
        sx={{
          position: 'absolute',
          top: `${tokens.spacing[3]}px`,
          left: `${tokens.spacing[3]}px`,
          zIndex: 2,
          // [수정: 디자인 개편 2026-09-20] 배지가 썸네일 위로 올라가므로 면이 불투명해야 한다.
          // 진행중/예정 상태는 alpha 13% 틴트만 있어 사진 위에서 배경이 비칠 보였다.
          // paper 면 + 동일 틴트 중첩으로 색은 그대로 두고 불투명하게만 만든다.
          bgcolor: ended ? PAPER2 : PAPER0,
          backgroundImage: ended
            ? 'none'
            : `linear-gradient(${alpha(meta.color, 0.13)}, ${alpha(meta.color, 0.13)})`,
          // 면은 meta.color, 10~11px 텍스트는 AA 충족값인 meta.textColor 를 쓴다.
          color: meta.textColor,
          borderRadius: `${tokens.radius.xl}px`,
          px: `${tokens.spacing[2]}px`,
          py: '3px',
          fontSize: 10.5,
          lineHeight: 1.4,
          fontWeight: 700,
          boxShadow: `0 2px 8px ${alpha(INK_HI, 0.28)}`,
        }}
      >
        {meta.label}
      </Box>
      <Box
        sx={{
          p: `${tokens.spacing[3]}px ${tokens.spacing[4]}px ${tokens.spacing[4]}px`,
          display: 'flex',
          flexDirection: 'column',
          gap: `${tokens.spacing[2]}px`,
          flex: 1,
          minWidth: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 800,
            lineHeight: 1.35,
            color: INK_HI,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            // [수정: 디자인 개편 2026-09-20] 2줄분(2.7em)을 무조건 예약해 한 줄짜리 제목
            // 아래에 빈 줄이 떠 있었다. 카드가 내용 높이로 붙으므로 예약을 푸고
            // 최대 2줄 제한(WebkitLineClamp)만 남긴다.
          }}
        >
          {festival.title}
        </Typography>
        {/* [수정: 디자인 개편 2026-09-20] 일정·지역이 각각 한 줄(24px × 2)을 써서
            카드가 불필요하게 길었다. 한 줄로 합치고 구분선으로 나눠 ~30px 회수. */}
        {(period || location) && (
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            {period && (
              <Typography
                sx={{
                  fontSize: 11.5,
                  lineHeight: 1.45,
                  color: INK_MID,
                  fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                  // JetBrains Mono 는 한글·공백에서 모노 폭을 먹어 "2026.  1.9  —  1.25" 처럼 벌어졌다.
                  wordSpacing: '-3.5px',
                  letterSpacing: '-0.2px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  flexShrink: 1,
                  minWidth: 0,
                }}
              >
                일정 · {period}
              </Typography>
            )}
            {period && location && (
              <Box
                aria-hidden="true"
                sx={{
                  flexShrink: 0,
                  width: '1px',
                  alignSelf: 'stretch',
                  bgcolor: BORDER,
                  mx: `${tokens.spacing[2]}px`,
                }}
              />
            )}
            {location && (
              <Typography
                sx={{
                  fontSize: 11.5,
                  lineHeight: 1.45,
                  color: INK_MID,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  flexShrink: 0,
                }}
              >
                지역 · {location.replace('강원특별자치도 ', '')}
              </Typography>
            )}
          </Box>
        )}
        {overview && (
          <Typography
            sx={{
              fontSize: 12.5,
              color: INK_MID,
              lineHeight: 1.65,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {overview}
          </Typography>
        )}
      </Box>
    </ButtonBase>
  );
}
